<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import authApi from '@/axios/authApi';
import axios from 'axios';
import { useAuthStore } from '@/stores/useAuthStore';
import { app_name } from '@/functions/utils';
import { useI18n } from 'vue-i18n';

const authStore = useAuthStore();
const route = useRoute();
const { t } = useI18n();

const tokenCheckInterval = ref<number | null>(null);

interface User {
  email: string;
  name: string;
  password: string;
  trigram: string;
  role: string;
}

const email = ref('');
const name = ref('');
const trigram = ref('');
const password = ref('');
const confirmPassword = ref('');
const errorMessage = ref('');
const successMessage = ref('');
const user = ref<User | null>(null);
const isRegistering = ref(false);
const showEditForm = ref(false);
const newName = ref('');
const newTrigram = ref('');
const newEmail = ref('');
const newPassword = ref('');

let isFetchingUser = false

function isTokenExpired(token: string): boolean {
  return !authStore.isTokenValid(token);
}

function checkToken() {
  tokenCheckInterval.value = setInterval(async () => {
    if (authStore.token && !isFetchingUser) {
      if (isTokenExpired(authStore.token)) {
        handleLogout();
      }
    }
  }, 30000); // Check every 30 seconds
}

function resetMessages() {
  errorMessage.value = '';
  successMessage.value = '';
}

const login = async () => {
  try {
    const response = await authApi.post(
      `/auth/login`,
      new URLSearchParams({
        username: email.value,
        password: password.value,
        scope: app_name
      }),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    );

    if (response.status === 200 && response.data.access_token) {
      authStore.setAuthData({
        token: response.data.access_token,
        refreshToken: response.data.refresh_token,
        email: email.value,
        name: '',
        scopes: [],
      });

      await fetchUser();
      resetMessages();
    } else {
      throw new Error(t('missingToken'));
    }
  } catch (error) {
    console.error('Login error:', error);
    errorMessage.value = t('errorLoginFailed');
  }
};

const register = async () => {
  try {
    if (password.value !== confirmPassword.value) {
      errorMessage.value = t('errorPasswordsMismatch');
      return;
    }

    const response = await authApi.post(
      `/users/users/`,
      {
        email: email.value,
        name: name.value,
        trigram: trigram.value,
        password: password.value,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (response.status === 201) {
      errorMessage.value = '';
      successMessage.value = t('successRegister');
      email.value = '';
      name.value = '';
      trigram.value = '';
      password.value = '';
      confirmPassword.value = '';
      isRegistering.value = false;
    }
  } catch (error: unknown) {
    console.error('Register error:', error);
    if (axios.isAxiosError(error)) {
      errorMessage.value = error.response?.data.detail || t('errorRegisterFailed');
    } else {
      errorMessage.value = t('defaultError');
    }
  }
};

const updateProfile = async () => {
  resetMessages();

  if (!newName.value.trim() && !newTrigram.value.trim() && !newEmail.value.trim() && !newPassword.value.trim()) {
    errorMessage.value = t('errorFillAtLeastOne');
    return;
  }

  if (newPassword.value && newPassword.value !== confirmPassword.value) {
    errorMessage.value = t('errorPasswordsMismatch');
    return;
  }

  if (newPassword.value && newPassword.value.length < 6) {
    errorMessage.value = t('errorPasswordMinLength');
    return;
  }

  if (newEmail.value && !newEmail.value.includes('@')) {
    errorMessage.value = t('errorInvalidEmail');
    return;
  }

  const payload: Record<string, string> = {};

  if (newName.value.trim()) payload.name = newName.value;
  if (newTrigram.value.trim()) payload.trigram = newTrigram.value;
  if (newEmail.value.trim()) payload.email = newEmail.value;
  if (newPassword.value.trim()) payload.password = newPassword.value;

  try {
    await authApi.patch(`/users/users/me`, payload, {
      headers: {
        Authorization: `Bearer ${authStore.token}`,
        'Content-Type': 'application/json',
      }
    });

    successMessage.value = t('successUpdate');
    errorMessage.value = '';
    newEmail.value = '';
    newName.value = '';
    newTrigram.value = '';
    newPassword.value = '';
    confirmPassword.value = '';
    await fetchUser();
  } catch (error) {
    console.error('Update profile error:', error);
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      errorMessage.value = t('sessionExpired');
    } else {
      errorMessage.value = t('errorUpdateFailed');
    }
  }
};

const fetchUser = async () => {
  if (isFetchingUser || !authStore.isAuthenticated) {
    return;
  }
  isFetchingUser = true;
  try {
    const response = await authApi.get(`/users/users/me`, {
      headers: { Authorization: `Bearer ${authStore.token}` }
    });

    authStore.setAuthData({
      token: authStore.token,
      refreshToken: authStore.refreshToken,
      email: authStore.email,
      name: response.data.name,
      scopes: response.data.scopes || []
    });

    user.value = {
      email: authStore.email,
      password: '',
      name: response.data.name.charAt(0).toUpperCase() + response.data.name.slice(1),
      trigram: response.data.trigram,
      role: response.data.role
    };
  } catch (error) {
    console.error('Fetch user error:', error);
    errorMessage.value = t('sessionExpired');
    handleLogout();
  } finally {
    isFetchingUser = false;
  }
};

const handleLogout = () => {
  authStore.logout();
  user.value = null;
  resetMessages();
};

onMounted(() => {
  checkToken();
});

onUnmounted(() => {
  if (tokenCheckInterval.value) {
    clearInterval(tokenCheckInterval.value);
    tokenCheckInterval.value = null;
  }
});



// Watch route changes for page navigation
watch(() => route.path, async () => {
  if (authStore.token && !isFetchingUser) {
    await fetchUser();
  }
  if (authStore.token && tokenCheckInterval.value) {
    clearInterval(tokenCheckInterval.value);
    tokenCheckInterval.value = null;
    checkToken();
  }
});

// Watch token changes
watch(() => authStore.token, async (newToken) => {
  if (isFetchingUser) {
    return;
  }
  if (!newToken) {
    handleLogout();
  } else if (isTokenExpired(newToken) && authStore.refreshToken) {
    await fetchUser();
  } else {
    await fetchUser();
  }
}, { immediate: true });
</script>

<template>
  <div :class="['auth-container', { 'wide': showEditForm }]">
    <div v-if="!authStore.isAuthenticated" class="auth-form">
      <h2 v-if="!isRegistering">{{ $t('login') }}</h2>
      <h2 v-else>{{ $t('createAccount') }}</h2>

      <!-- Formulaire de connexion -->
      <form v-if="!isRegistering" @submit.prevent="login" :key="'login-form'">
        <input id="email" v-model="email" type="email" :placeholder="$t('emailPlaceholder')" autocomplete="username"
          class="input-auth" />
        <input id="password" v-model="password" type="password" :placeholder="$t('passwordPlaceholder')"
          autocomplete="current-password" class="input-auth" />
        <button type="submit">{{ $t('loginButton') }}</button>
        <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
        <p v-if="successMessage" class="success">{{ successMessage }}</p>
        <p>{{ $t('noAccount') }}<a href="#" @click="isRegistering = true; resetMessages()">{{ $t('createAccount') }}</a>
        </p>
      </form>

      <!-- Formulaire d'inscription -->
      <form v-else @submit.prevent="register" :key="'register-form'">
        <input v-model="email" type="email" :placeholder="$t('emailPlaceholder')" autocomplete="username"
          class="input-auth" />
        <input v-model="name" type="name" :placeholder="$t('namePlaceholder')" class="input-auth" />
        <input v-model="trigram" type="trigram" :placeholder="$t('trigramPlaceholder')" class="input-auth" />
        <input v-model="password" type="password" :placeholder="$t('passwordPlaceholder')" autocomplete="new-password"
          class="input-auth" />
        <input v-model="confirmPassword" type="password" :placeholder="$t('confirmPasswordPlaceholder')"
          autocomplete="new-password" class="input-auth" />
        <button type="submit">{{ $t('registerButton') }}</button>
        <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
        <p>{{ $t('haveAccount') }}<a href="#" @click="isRegistering = false; resetMessages()">{{ $t('login') }}</a></p>
      </form>
    </div>

    <div v-else class="user-info">
      <h2>{{ $t('welcome') }} {{ user?.name }}</h2>
      <button @click="showEditForm = !showEditForm; resetMessages()">
        {{ showEditForm ? $t('editProfileButton.hide') : $t('editProfileButton.show') }}
      </button>


      <div v-if="showEditForm" class="edit-form">
        <div class="user-details">
          <p><strong>{{ $t('namePlaceholder') }} :</strong> {{ user?.name }}</p>
          <p><strong>{{ $t('trigramPlaceholder') }} :</strong> {{ user?.trigram.toLocaleUpperCase() }}</p>
          <p><strong>{{ $t('emailPlaceholder') }} :</strong> {{ user?.email }}</p>
          <p><strong>{{ $t('roleLabel') }} :</strong> {{ user?.role }}</p>
        </div>

        <div>
          <h2>{{ $t('updateProfileTitle') }}</h2>
          <form class="form-grid">
            <label for="newName">{{ $t('namePlaceholder') }}</label>
            <input id="newName" v-model="newName" class="input-auth" type="text" :placeholder="$t('namePlaceholder')"
              autocomplete="name" />

            <label for="newTrigram">{{ $t('trigramPlaceholder') }}</label>
            <input id="newTrigram" v-model="newTrigram" class="input-auth" type="text"
              :placeholder="$t('trigramPlaceholder')" />

            <label for="newEmail">{{ $t('emailPlaceholder') }}</label>
            <input id="newEmail" v-model="newEmail" class="input-auth" type="email"
              :placeholder="$t('emailPlaceholder')" autocomplete="email" />

            <label for="newPassword">{{ $t('passwordPlaceholder') }}</label>
            <input id="newPassword" v-model="newPassword" class="input-auth" type="password"
              :placeholder="$t('passwordPlaceholder')" autocomplete="new-password" />

            <label for="confirmPassword">{{ $t('confirmPasswordPlaceholder') }}</label>
            <input id="confirmPassword" v-model="confirmPassword" class="input-auth" type="password"
              :placeholder="$t('passwordPlaceholder')" autocomplete="new-password" />
          </form>

          <button @click.prevent="updateProfile">{{ $t('saveChangesButton') }}</button>

          <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
          <p v-if="successMessage" class="success">{{ successMessage }}</p>
        </div>
      </div>
      <button @click="handleLogout">{{ $t('logoutButton') }}</button>
    </div>
  </div>
</template>


<style scoped>
.auth-container {
  width: 100%;
  max-width: 400px;
  padding: 30px;
  border: 1px solid var(--color-main);
  background-color: var(--color-bg);
  color: var(--color-main);
  border-radius: var(--radius);
}

h2 {
  text-align: center;
  margin-bottom: 24px;
  color: var(--color-main);
}

form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;
  align-items: center;
}

.form-grid label {
  text-align: right;
  padding-right: 8px;
  color: var(--color-main);
}

.form-grid input {
  width: 100%;
}

.input-auth {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--color-main);
  background-color: var(--color-bg);
  color: var(--color-main);
}

.input-auth:focus {
  outline: none;
  border-color: var(--color-main);
  background-color: var(--color-bg);
  ;
}

button {
  padding: 12px;
  background-color: var(--color-main);
  color: var(--color-bg);
  border: 1px solid var(--color-main);
  font-size: 15px;
  cursor: pointer;
  font-weight: bold;
  width: 100%;
}

button:hover {
  background-color: var(--color-success);
}

p {
  text-align: center;
  font-size: 14px;
  margin: 0;
}

p.error {
  color: var(--color-error);
  font-weight: bold;
}

p.success {
  color: var(--color-success);
  font-weight: bold;
}

a {
  color: var(--color-main);
  text-decoration: none;
  font-weight: bold;
}

a:hover {
  text-decoration: underline;
}

.user-info {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
}

.edit-form {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.auth-container.wide {
  max-width: 800px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 24px;
  background-color: var(--color-bg);
}
</style>
